import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { MessageSquarePlus, X, Bug, Lightbulb, HelpCircle, MessageCircle, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-500', desc: 'Something isn\'t working' },
  { id: 'suggestion', label: 'Suggestion', icon: Lightbulb, color: 'text-yellow-500', desc: 'Idea for improvement' },
  { id: 'question', label: 'Question', icon: HelpCircle, color: 'text-blue-500', desc: 'Need help with something' },
  { id: 'general', label: 'General', icon: MessageCircle, color: 'text-green-500', desc: 'Other feedback' },
];

export default function FeedbackButton() {
  const { user, token } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  // Don't render if user is not logged in
  if (!user || !token) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!category) {
      toast.error('Please select a category');
      return;
    }
    if (!subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!message.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/beta-feedback`, {
        category,
        subject: subject.trim(),
        message: message.trim(),
        page_context: window.location.pathname
      });

      if (response.data.success) {
        toast.success('Thank you for your feedback!');
        setIsOpen(false);
        // Reset form
        setCategory('');
        setSubject('');
        setMessage('');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setIsOpen(false);
    setCategory('');
    setSubject('');
    setMessage('');
  };

  return (
    <>
      {/* Floating Feedback Button - positioned above sidebar footer */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-16 left-6 z-50 flex items-center gap-2 px-4 py-3 bg-orange-600 hover:bg-orange-500 text-white rounded-full shadow-lg transition-all hover:scale-105 group"
        data-testid="feedback-button"
      >
        <MessageSquarePlus className="w-5 h-5" />
        <span className="font-medium">Feedback</span>
      </button>

      {/* Feedback Modal */}
      <Dialog open={isOpen} onOpenChange={resetAndClose}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="feedback-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-orange-500" />
              Beta Feedback
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Help us improve CNC-Helpdesk! Your feedback is valuable.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Category Selection */}
            <div className="space-y-2">
              <Label className="text-zinc-300">What type of feedback?</Label>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const isSelected = category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategory(cat.id)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-all ${
                        isSelected 
                          ? 'border-orange-500 bg-orange-500/10' 
                          : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                      }`}
                      data-testid={`category-${cat.id}`}
                    >
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-orange-500' : cat.color}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-orange-500' : 'text-zinc-300'}`}>
                        {cat.label}
                      </span>
                      <span className="text-xs text-zinc-500">{cat.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject" className="text-zinc-300">Subject</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your feedback"
                className="bg-zinc-800 border-zinc-700 text-white"
                maxLength={100}
                data-testid="feedback-subject"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message" className="text-zinc-300">Your Feedback</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Please describe your feedback in detail..."
                className="bg-zinc-800 border-zinc-700 text-white min-h-[120px] resize-none"
                maxLength={2000}
                data-testid="feedback-message"
              />
              <p className="text-xs text-zinc-500 text-right">{message.length}/2000</p>
            </div>

            {/* Context Info */}
            <p className="text-xs text-zinc-500">
              Submitting from: <span className="text-zinc-400">{window.location.pathname}</span>
            </p>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={resetAndClose}
                className="flex-1 border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-orange-600 hover:bg-orange-500"
                data-testid="submit-feedback"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Feedback
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
